import { removeHiddenChars } from "../removeHiddenChars";
import { getReadingFluencyFromCustomData, mergeReadingFluencyCustomData } from "../../const/fluencyCustomData";

/**
 * Normalizes a comment value for comparison and persistence.
 * Strips hidden characters, trims whitespace, and converts empty string to null.
 * Null means "no comment" to the server.
 *
 * @param {string|null|undefined} comment
 * @returns {string|null}
 */
export const normalizeComment = (comment) => {
    const cleaned = removeHiddenChars(String(comment ?? "").trim());
    return cleaned === "" ? null : cleaned;
};

export const normalizeReadingFluency = (value) => String(value ?? "").trim();

/**
 * Builds the delta comment array sent to the server on comment save.
 *
 * Strategy: DELTA only — only records whose comment or readingFluency has
 * changed vs. the last known server state are included. This is intentional
 * server behavior and must NOT be unified with the full-state ratings save.
 *
 * A record is included when:
 *   - It is new (no existing server record) AND has a non-null comment OR non-empty readingFluency, OR
 *   - It exists on the server AND the normalized comment or readingFluency has changed
 *
 * @param {Object} params
 * @param {Map<string, CommentMapEntry>} params.commentMap
 *   Value may include readingFluency (UI WPM string).
 * @param {Array<ExistingComment>} params.existingComments
 *   May include customData.readingFluency.
 * @param {number} params.updaterGuid - current user's guid (lastUpdaterGuid)
 *
 * @returns {CommentRecord[]}
 */
const buildCommentPayload = ({ commentMap, existingComments, updaterGuid }) => {
    const records = [];

    commentMap.forEach((entry) => {
        const newComment = normalizeComment(entry.comment);
        const newFluency = normalizeReadingFluency(entry.readingFluency);

        const existing = (existingComments ?? []).find(
            (e) =>
                String(e.gradingProfileStudentId) === String(entry.gradingProfileStudentId) &&
                String(e.gradingCriteria) === String(entry.gradingCriteria)
        );

        const existingComment = normalizeComment(existing?.comment);
        const existingFluency = getReadingFluencyFromCustomData(existing?.customData);

        const commentChanged = existingComment !== newComment;
        const fluencyChanged = existingFluency !== newFluency;

        if (existing && !commentChanged && !fluencyChanged) return;

        // Skip new records with nothing to persist
        if (!existing && newComment === null && !newFluency) return;

        const record = {
            key: existing?.key ?? null,
            gradingProfileStudentId: entry.gradingProfileStudentId,
            gradingCriteria: entry.gradingCriteria,
            comment: newComment,
            lastUpdaterGuid: updaterGuid
        };

        const customData = mergeReadingFluencyCustomData(existing?.customData, newFluency);
        if (customData) {
            record.customData = customData;
        } else if (fluencyChanged) {
            // Explicit empty object so the BE can clear readingFluency.
            record.customData = {};
        }

        records.push(record);
    });

    return records;
};

export default buildCommentPayload;
